import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useQuery, useMutation, useQueryClient, type UseQueryOptions, type UseMutationOptions } from '@tanstack/react-query';
import { WisecureApiClient } from './index';
import { createWisecureClient, configureOpenAPI, type WisecureClientOptions } from './createClient';

const WisecureContext = createContext<WisecureApiClient | null>(null);

export interface WisecureProviderProps extends WisecureClientOptions {
  children: ReactNode;
}

export function WisecureProvider({ children, ...options }: WisecureProviderProps) {
  const client = useMemo(() => {
    configureOpenAPI(options);
    return createWisecureClient(options);
  }, [options.baseUrl, options.token]);

  return (
    <WisecureContext.Provider value={client}>
      {children}
    </WisecureContext.Provider>
  );
}

export function useWisecureClient(): WisecureApiClient {
  const client = useContext(WisecureContext);
  if (!client) {
    throw new Error('useWisecureClient must be used within a WisecureProvider');
  }
  return client;
}

export function useWisecureQuery<TData>(
  key: readonly unknown[],
  fetcher: (client: WisecureApiClient) => Promise<TData>,
  options?: Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn'>,
) {
  const client = useWisecureClient();
  return useQuery<TData>({
    queryKey: key,
    queryFn: () => fetcher(client),
    ...options,
  });
}

export function useWisecureMutation<TData, TVariables = void>(
  fetcher: (client: WisecureApiClient, variables: TVariables) => Promise<TData>,
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'>,
) {
  const client = useWisecureClient();
  return useMutation<TData, Error, TVariables>({
    mutationFn: (variables) => fetcher(client, variables),
    ...options,
  });
}

export function useInvalidateQueries() {
  const queryClient = useQueryClient();
  return (key: readonly unknown[]) =>
    queryClient.invalidateQueries({ queryKey: key });
}
